import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Gift, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ProductCard from '@/components/ProductCard';
import SignupModal from '@/components/SignupModal';
import useCart from '@/store/cartStore';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  const addItem = useCart((state) => state.addItem);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const [featuredRes, allRes] = await Promise.all([
        axios.get(`${API}/products/featured`),
        axios.get(`${API}/products`)
      ]);
      setFeaturedProducts(featuredRes.data);
      setProducts(allRes.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Find the Grower's Pick bundle specifically
  const growersPick = products.find(p => 
    p.name.toLowerCase().includes("grower's pick") || 
    p.name.toLowerCase().includes("growers pick")
  );

  const handleAddToCart = async (product) => {
    const success = await addItem({ ...product, id: product._id }, 1);
    if (success) {
      toast.success(`${product.name} added to cart!`, { duration: 1000 });
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      {/* Hero Section with Prominent Branding */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-cyan-50/50 py-8 md:py-16 lg:py-24 overflow-hidden" data-testid="hero-section">
        {/* Ichthys (Jesus fish) - subtle placement top right */}
        <div className="absolute top-4 right-4 md:right-8 lg:right-12 z-20">
          <svg 
            viewBox="0 0 64 32" 
            className="w-8 h-4 md:w-10 md:h-5 opacity-50 hover:opacity-70 transition-opacity"
            fill="none"
            stroke="#92724a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 16 C16 2, 40 2, 50 16 C40 30, 16 30, 2 16 Z" />
            <path d="M50 16 C56 10, 62 8, 62 8" />
            <path d="M50 16 C56 22, 62 24, 62 24" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl relative z-10">
          {/* Large Logo Display */}
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14 lg:gap-20">
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="flex items-center gap-1.5 mb-3 text-sm">
                <span className="text-yellow-500 text-base">★★★★★</span>
                <span className="text-muted-foreground">on Google</span>
              </div>
              <img 
                src="https://aquaplantsplus.square.site/uploads/b/92824204f5eb3a1741c28b0b59a1630abf175e712898d2f2d5a8e8dc409472cf/AquaPlantsPlusLogo_1762231535.jpeg"
                alt="AquaPlants Plus Logo"
                className="w-40 h-40 md:w-52 md:h-52 lg:w-64 lg:h-64 rounded-3xl shadow-2xl shadow-primary/20 object-contain bg-white ring-4 ring-white"
                data-testid="hero-logo"
              />
            </div>
            <div className="text-center lg:text-left max-w-xl lg:max-w-2xl">
              <p className="text-[11px] md:text-xs text-muted-foreground/70 mb-4 md:mb-5 tracking-[0.25em] uppercase font-medium" data-testid="hero-brow">
                Missouri-Grown&nbsp;&nbsp;•&nbsp;&nbsp;Aquarium-Ready
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-4 md:mb-5" data-testid="hero-title">
                <span className="text-primary">AquaPlants</span>
                <span className="text-foreground/60 ml-1 font-light">Plus</span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-foreground/80 mb-5 md:mb-6 font-medium" data-testid="hero-tagline">
                We grow plants you'll love.
              </p>
              <p className="text-sm md:text-base text-muted-foreground mb-8 md:mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Premium live freshwater plants, shrimp, and other invertebrates—shipped fresh from{' '}
                <span className="text-primary font-semibold">Springfield, MO</span>. 
                No middlemen, no mystery sources.
              </p>
              <Link to="/shop">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 md:h-14 px-8 md:px-10 rounded-full font-medium transition-all hover:scale-105 shadow-lg shadow-primary/20 text-sm md:text-base" data-testid="shop-now-btn">
                  Shop Now <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HERO BUNDLE - Grower's Pick Prominent Display */}
      {growersPick && (
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary via-teal-600 to-primary" data-testid="hero-bundle-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-44 md:h-44 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                  <img 
                    src={growersPick.image_url} 
                    alt={growersPick.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-white/80 text-sm uppercase tracking-wider mb-1">⭐ Best Seller</p>
                <h2 className="heading-font text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
                  {growersPick.name}
                </h2>
                <p className="text-white/90 text-base md:text-lg mb-4 max-w-xl">
                  {growersPick.description}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-3">
                    {growersPick.sale_price && growersPick.sale_price < growersPick.price ? (
                      <>
                        <span className="text-xl md:text-2xl text-white/60 line-through">
                          ${growersPick.price.toFixed(2)}
                        </span>
                        <span className="text-3xl md:text-4xl font-bold text-red-600">
                          ${growersPick.sale_price.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="text-3xl md:text-4xl font-bold text-white">
                        ${growersPick.price.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <Button 
                    onClick={() => handleAddToCart(growersPick)}
                    disabled={growersPick.inventory_quantity <= 0}
                    className={`${growersPick.inventory_quantity <= 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-white text-primary hover:bg-white/90'} rounded-full h-12 px-8 font-semibold shadow-lg`}
                    data-testid="hero-bundle-add-cart"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {growersPick.inventory_quantity <= 0 ? 'Sold Out' : 'Add to Cart'}
                  </Button>
                  <Link to={`/product/${growersPick._id}`}>
                    <Button variant="outline" className="border-white/50 text-white hover:bg-white/10 rounded-full">
                      View Details
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Bundles Section */}
      {featuredProducts.length > 0 && (
        <section className="py-12 md:py-20" data-testid="featured-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">Top Sellers</p>
                <h2 className="heading-font text-3xl md:text-4xl font-semibold" data-testid="featured-title">Featured Bundles</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {featuredProducts.map((product) => (
                <div key={product._id} className="group relative bg-card rounded-3xl border border-border/50 overflow-hidden hover:shadow-2xl hover:shadow-green-900/10 transition-all duration-500" data-testid={`featured-bundle-${product._id}`}>
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    {/* Out of Stock Badge */}
                    {product.inventory_quantity <= 0 && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Out of Stock
                      </div>
                    )}
                    {/* Low Stock Badge */}
                    {product.inventory_quantity > 0 && product.inventory_quantity <= 5 && (
                      <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Only {product.inventory_quantity} left!
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
                      <p className="text-white/80 text-xs md:text-sm uppercase tracking-wider mb-1 md:mb-2">{product.category}</p>
                      <h3 className="heading-font text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 line-clamp-2">{product.name}</h3>
                      <p className="text-white/90 text-sm md:text-base mb-3 line-clamp-2">{product.description}</p>
                      <div className="flex items-center gap-3 md:gap-4">
                        {product.sale_price && product.sale_price < product.price ? (
                          <>
                            <span className="text-lg md:text-xl text-white/60 line-through">${product.price.toFixed(2)}</span>
                            <span className="text-xl md:text-2xl lg:text-3xl font-bold text-red-600">${product.sale_price.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-xl md:text-2xl lg:text-3xl font-bold text-white">${product.price.toFixed(2)}</span>
                        )}
                        {product.inventory_quantity > 0 ? (
                          <Button 
                            onClick={() => handleAddToCart(product)}
                            className="bg-white text-primary hover:bg-white/90 rounded-full text-sm" 
                            data-testid={`add-bundle-${product._id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add to Cart
                          </Button>
                        ) : (
                          <Button 
                            disabled
                            className="bg-gray-400 text-white rounded-full text-sm cursor-not-allowed" 
                            data-testid={`add-bundle-${product._id}`}
                          >
                            Sold Out
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products by Category */}
      <section className="py-12 md:py-20 bg-secondary/30" data-testid="products-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
            <div key={category} className="mb-12 md:mb-16 last:mb-0" data-testid={`category-${category}`}>
              <h2 className="heading-font text-2xl md:text-3xl lg:text-4xl font-semibold mb-6 md:mb-8 capitalize">{category}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {categoryProducts.slice(0, 8).map((product) => (
                  <ProductCard key={product._id} product={{ ...product, id: product._id }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter/Signup Section - Combined with Rewards */}
      <section className="py-12 md:py-20" data-testid="newsletter-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <Card className="bg-gradient-to-br from-primary via-green-600 to-emerald-700 rounded-3xl p-6 md:p-10 lg:p-12 relative overflow-hidden" data-testid="signup-callout">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4xIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-10" />
            
            <div className="relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Left side - Main CTA */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full mb-4">
                    <Gift className="w-4 h-4" />
                    <span className="text-sm font-semibold">New Customer Offer</span>
                  </div>
                  <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                    15% Off + Plant Perks
                  </h2>
                  <p className="text-white/90 text-base md:text-lg mb-6 max-w-xl">
                    Join AquaPlants Plus and get 15% off your first order. Plus, start earning Plant Perks on every purchase!
                  </p>
                  <Button 
                    onClick={() => setShowSignup(true)}
                    className="bg-white text-primary hover:bg-white/90 h-12 md:h-14 px-8 md:px-10 rounded-full font-semibold text-base md:text-lg shadow-lg" 
                    data-testid="signup-cta-btn"
                  >
                    Join Now & Save
                  </Button>
                </div>
                
                {/* Right side - Plant Perks info */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-white/20">
                  <h3 className="heading-font text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">🌿</span> Plant Perks
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">2x</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold">Earn 2 Perks Per $1</p>
                        <p className="text-white/70 text-sm">Perks add up fast on every purchase</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold">$5</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold">Redeem 100 Perks = $5 Off</p>
                        <p className="text-white/70 text-sm">Use your perks at checkout</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-400/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-lg">🎁</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold">Exclusive Member Perks</p>
                        <p className="text-white/70 text-sm">Early access to sales & new arrivals</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
      
      <SignupModal open={showSignup} onOpenChange={setShowSignup} />
    </div>
  );
};

export default Home;
